// src/SkillTracker.jsx
import React, { useState, useMemo, useEffect } from "react";
import "./SkillTracker.css";
import API_BASE from "../config/api";


// ===== PLAN DEFINITION (your roadmap) =====
const PLAN = [
  {
    id: "phase1",
    title: "Phase 1 — SQL Foundations",
    description: "Master SQL for DA interviews and real business work.",
    sections: [
      {
        id: "sql-core",
        title: "SQL Core",
        items: [
          { id: "sql-select-where", label: "SELECT, WHERE", addToSkills: true },
          { id: "sql-order-group", label: "ORDER BY, GROUP BY, HAVING", addToSkills: true },
          { id: "sql-joins", label: "INNER / LEFT / RIGHT / FULL joins", addToSkills: true },
          { id: "sql-cross-union", label: "CROSS JOIN, UNION vs UNION ALL", addToSkills: true },
          { id: "sql-subqueries", label: "Subqueries & nested subqueries", addToSkills: true },
          { id: "sql-cte", label: "CTEs (WITH)", addToSkills: true },
        ],
      },
      {
        id: "sql-window",
        title: "Window & Functions",
        items: [
          {
            id: "sql-window-fns",
            label:
              "Window functions (ROW_NUMBER, RANK, DENSE_RANK, LAG/LEAD, SUM() OVER)",
            addToSkills: true,
          },
          { id: "sql-date-fns", label: "Date / time functions", addToSkills: true },
          { id: "sql-string-fns", label: "String functions", addToSkills: true },
          { id: "sql-agg-fns", label: "Aggregate functions", addToSkills: true },
          { id: "sql-case-when", label: "CASE WHEN logic", addToSkills: true },
          { id: "sql-temp-tables", label: "Temporary tables", addToSkills: true },
          { id: "sql-optimization", label: "Query optimization basics", addToSkills: true },
        ],
      },
      {
        id: "sql-projects",
        title: "Practice & Projects",
        items: [
          { id: "sql-300-questions", label: "300+ SQL practice questions", addToSkills: false },
          { id: "sql-business-cases", label: "Real business case problems", addToSkills: false },
          { id: "sql-3-projects", label: "3 SQL projects built", addToSkills: false },
        ],
      },
    ],
    
  },
  {
    id: "phase2",
    title: "Phase 2 — Python & Forecasting",
    description: "Python, Pandas, NumPy, Viz, ML, and forecasting.",
    sections: [
      {
        id: "py-core",
        title: "Python Core",
        items: [
          {
            id: "py-core-basics",
            label: "Data types, loops, functions, error & file handling",
            addToSkills: true,
          },
        ],
      },
      {
        id: "py-pandas",
        title: "Pandas",
        items: [
          {
            id: "pd-cleaning",
            label: "Cleaning, handling missing values & outliers",
            addToSkills: true,
          },
          {
            id: "pd-merge-groupby",
            label: "Merging, GroupBy, pivot tables, multi-index",
            addToSkills: true,
          },
          {
            id: "pd-timeseries",
            label: "Time-series handling in Pandas",
            addToSkills: true,
          },
          {
            id: "pd-apply-lambda",
            label: "apply / map / lambda patterns",
            addToSkills: true,
          },
        ],
      },
      {
        id: "py-numpy",
        title: "NumPy",
        items: [
          {
            id: "np-arrays",
            label: "Arrays, indexing, broadcasting, vectorized ops",
            addToSkills: true,
          },
        ],
      },
      {
        id: "py-viz",
        title: "Visualization",
        items: [
          {
            id: "viz-plots",
            label:
              "Matplotlib & Seaborn: histograms, boxplots, line charts, heatmaps",
            addToSkills: true,
          },
          {
            id: "viz-trends",
            label: "Trend analysis from time-series plots",
            addToSkills: true,
          },
        ],
      },
      {
        id: "py-ml",
        title: "ML Basics",
        items: [
          {
            id: "ml-regression",
            label: "Linear & logistic regression",
            addToSkills: true,
          },
          {
            id: "ml-workflow",
            label: "Train/test, scaling, feature engineering, evaluation",
            addToSkills: true,
          },
        ],
      },
      {
        id: "py-forecast",
        title: "Time-Series Forecasting",
        items: [
          {
            id: "ts-forecast-models",
            label: "ARIMA, SARIMA, Prophet",
            addToSkills: true,
          },
          {
            id: "ts-anomaly",
            label: "Moving averages, seasonal decomposition, anomaly detection",
            addToSkills: true,
          },
        ],
      },
      {
        id: "py-projects",
        title: "Python Projects",
        items: [
          {
            id: "proj-cleaning-pipeline",
            label: "Data cleaning pipeline project",
            addToSkills: false,
          },
          {
            id: "proj-api-to-sql",
            label: "API → SQL pipeline project",
            addToSkills: false,
          },
          {
            id: "proj-forecasting",
            label: "Forecasting project",
            addToSkills: false,
          },
          {
            id: "proj-end-to-end",
            label: "End-to-end project (Python → SQL → dashboard)",
            addToSkills: false,
          },
        ],
      },
    ],
  },
  {
    id: "phase3",
    title: "Phase 3 — Business Tools (Power BI + Excel)",
    description: "Dashboards, DAX, Excel advanced formulas.",
    sections: [
      {
        id: "pbi-core",
        title: "Power BI",
        items: [
          {
            id: "pbi-modeling",
            label: "Data modeling, relationships, star schema",
            addToSkills: true,
          },
          {
            id: "pbi-dax-core",
            label: "Core DAX: CALCULATE, FILTER, ALL, SUMX, DIVIDE, VAR",
            addToSkills: true,
          },
          {
            id: "pbi-time-intel",
            label: "Time-intel: YTD, MTD, QoQ, rolling 12 months",
            addToSkills: true,
          },
          {
            id: "pbi-kpi",
            label: "KPI dashboards, cards, drill-down, tooltips",
            addToSkills: true,
          },
          {
            id: "pbi-rls",
            label: "Row-level security",
            addToSkills: true,
          },
          {
            id: "pbi-3-dashboards",
            label: "3 dashboards (sales, finance, Sun Auto ops)",
            addToSkills: false,
          },
        ],
      },
      {
        id: "excel-adv",
        title: "Excel (Advanced)",
        items: [
          {
            id: "xl-lookups",
            label: "XLOOKUP, INDEX/MATCH",
            addToSkills: true,
          },
          {
            id: "xl-agg",
            label: "SUMIFS, COUNTIFS, IFERROR, nested formulas",
            addToSkills: true,
          },
          {
            id: "xl-pivot-powerquery",
            label: "Pivot tables, Power Query, data cleaning",
            addToSkills: true,
          },
          {
            id: "xl-forecast",
            label: "Forecast Sheet, what-if analysis",
            addToSkills: true,
          },
        ],
      },
      {
      id: "phase3-projects",
      title: "Projects",
      items: [
        { id: "p3-finance-dashboard", label: "Finance Power BI Dashboard", addToSkills: false },
        { id: "p3-ops-dashboard", label: "Operations KPI Dashboard", addToSkills: false },
        { id: "p3-pq-excel", label: "Excel Power Query Cleaning + Pivot Report", addToSkills: false }
      ]
    }
    ],
  },
  {
    id: "phase4",
    title: "Phase 4 — ETL & Pipelines",
    description: "Automate data movement and cleaning.",
    sections: [
      {
        id: "etl-core",
        title: "ETL Basics",
        items: [
          {
            id: "etl-steps",
            label: "Extract → clean → normalize → validate → load to SQL",
            addToSkills: true,
          },
          {
            id: "etl-automation",
            label: "Automate pipelines, modular Python, incremental loads",
            addToSkills: true,
          },
          {
            id: "etl-daily-pipeline",
            label: "Daily pipeline (API → Python → SQL → Power BI)",
            addToSkills: true,
          },
        ],
      },{
      id: "phase4-projects",
      title: "Projects",
      items: [
        { id: "p4-daily-pipeline", label: "Daily Automated Pipeline (API → Clean → SQL)", addToSkills: false },
        { id: "p4-incremental", label: "Incremental Load ETL Pipeline", addToSkills: false },
        { id: "p4-modular-etl", label: "Modular ETL Framework (functions + logs)", addToSkills: false }
      ]
    }
    ],
  },
  {
    id: "phase5",
    title: "Phase 5 — Cloud (AWS Preferred)",
    description: "Deploy data workflows in the cloud.",
    sections: [
      {
        id: "cloud-aws",
        title: "AWS Core Services",
        items: [
          { id: "aws-s3", label: "S3 (storage)", addToSkills: true },
          { id: "aws-lambda", label: "Lambda (serverless)", addToSkills: true },
          { id: "aws-glue", label: "Glue (ETL)", addToSkills: true },
          { id: "aws-athena", label: "Athena (SQL on S3)", addToSkills: true },
          { id: "aws-rds", label: "RDS (database)", addToSkills: true },
          { id: "aws-iam", label: "IAM (permissions & roles)", addToSkills: true },
        ],
      },
      {
        id: "cloud-project",
        title: "Cloud Project",
        items: [
          { id: "p5-s3-athena", label: "S3 → Athena → Power BI Pipeline", addToSkills: false },
          { id: "p5-lambda-clean", label: "Lambda-triggered Cleaning Script", addToSkills: false },
          { id: "p5-rds-db", label: "AWS RDS SQL Database + ETL", addToSkills: false }
        ]
      },
    ],
  },
  {
    id: "phase6",
    title: "Phase 6 — Data Engineering Basics",
    description: "DB design and internals to stand out.",
    sections: [
      {
        id: "de-core",
        title: "Database Concepts",
        items: [
          {
            id: "db-how-works",
            label: "How databases & indexes work",
            addToSkills: true,
          },
          { id: "db-acid", label: "ACID concept", addToSkills: true },
          { id: "db-erd", label: "ERD design", addToSkills: true },
          {
            id: "db-normalization",
            label: "Normalization (1NF / 2NF / 3NF)",
            addToSkills: true,
          },
        ],
      },
      {
        id: "de-project",
        title: "DB Project",
        items: [
          {id: "db-own",label: "Own SQL DB: customers, orders, products, metrics",addToSkills: false,},
           { id: "p6-star-schema", label: "Star Schema Data Warehouse", addToSkills: false },
          { id: "p6-index-compare", label: "Index Optimization Comparison", addToSkills: false }
        ],
      },
    ],
  },
  {
    id: "phase7",
    title: "Phase 7 — Statistics for Data Analysis",
    description: "Stats you actually use to answer business questions.",
    sections: [
      {
        id: "stats-core",
        title: "Core Stats",
        items: [
          {
            id: "stats-basics",
            label: "Mean, median, mode, variance, SD, correlation",
            addToSkills: true,
          },
          { id: "stats-prob", label: "Probability basics", addToSkills: true },
          {
            id: "stats-inference",
            label: "Hypothesis testing, p-values, confidence intervals",
            addToSkills: true,
          },
          {
            id: "stats-advanced",
            label: "ANOVA, chi-square, A/B testing",
            addToSkills: true,
          },
          {
            id: "stats-regression",
            label: "Regression interpretation",
            addToSkills: true,
          },
        ],
      },
      {
      id: "phase7-projects",
      title: "Projects",
      items: [
        { id: "p7-ab-test", label: "A/B Test Analysis Project", addToSkills: false },
        { id: "p7-regression", label: "Regression Interpretation Case Study", addToSkills: false },
        { id: "p7-correlation", label: "Correlation & Variance Study", addToSkills: false }
      ]
    }
    ],
  },
  {
    id: "phase8",
    title: "Phase 8 — Portfolio",
    description: "6–8 master projects public and clean.",
    sections: [
      {
        id: "portfolio-projects",
        title: "Projects",
        items: [
          {
            id: "port-sql-case-study",
            label: "SQL case study (advanced)",
            addToSkills: false,
          },
          {
            id: "port-py-cleaning",
            label: "Python cleaning pipeline",
            addToSkills: false,
          },
          {
            id: "port-api-data",
            label: "API data project",
            addToSkills: false,
          },
          {
            id: "port-power-bi",
            label: "Power BI dashboard",
            addToSkills: false,
          },
          {
            id: "port-forecast",
            label: "Forecasting model",
            addToSkills: false,
          },
          {
            id: "port-end-to-end",
            label: "End-to-end (Python → SQL → Power BI)",
            addToSkills: false,
          },
          {
            id: "port-cloud",
            label: "Cloud data pipeline (bonus)",
            addToSkills: false,
          },
          {
            id: "port-ops-analytics",
            label: "Operations analytics (Sun Auto)",
            addToSkills: false,
          },
        ],
      },
    ],
  },
  {
    id: "phase9",
    title: "Phase 9 — LinkedIn & GitHub",
    description: "Show your work and attract recruiters.",
    sections: [
      {
        id: "linkedin",
        title: "LinkedIn",
        items: [
          {
            id: "li-profile",
            label: "Strong headline, banner, about",
            addToSkills: true,
          },
          {
            id: "li-posting",
            label: "Post 2–3 times per week with visuals",
            addToSkills: true,
          },
          {
            id: "li-featured",
            label: "Projects in FEATURED section",
            addToSkills: true,
          },
          {
            id: "li-network",
            label: "Connect with recruiters & hiring managers",
            addToSkills: true,
          },
        ],
      },
      {
        id: "github",
        title: "GitHub",
        items: [
          {
            id: "gh-repos",
            label: "One repo per project, clean structure",
            addToSkills: true,
          },
          {
            id: "gh-readme",
            label: "Clear README, notebooks, scripts, images",
            addToSkills: true,
          },
        ],
      },
    ],
  },
  {
    id: "phase10",
    title: "Phase 10 — Job Hunt System",
    description: "Daily execution until offer.",
    sections: [
      {
        id: "job-hunt-core",
        title: "Daily Job Hunt",
        items: [
          {
            id: "jh-apply-volume",
            label: "Apply to ~10 jobs/day (first 24h)",
            addToSkills: false,
          },
          {
            id: "jh-message-recruiter",
            label: "Message recruiter each time",
            addToSkills: false,
          },
          {
            id: "jh-custom-resume",
            label: "Customize resume per job",
            addToSkills: false,
          },
          {
            id: "jh-track-apps",
            label: "Track all applications (your system)",
            addToSkills: false,
          },
          {
            id: "jh-interview-prep",
            label: "Daily interview prep: SQL, Python, DAX, behavioral",
            addToSkills: true,
          },
        ],
      },
    ],
  },
  {
    id: "phase11",
    title: "Phase 11 — Weekly Routine",
    description: "Keep everything warm while searching.",
    sections: [
      {
        id: "routine-weekday",
        title: "Mon–Fri",
        items: [
          { id: "rt-sql", label: "1h SQL", addToSkills: false },
          { id: "rt-python", label: "1h Python", addToSkills: false },
          { id: "rt-powerbi", label: "1h Power BI", addToSkills: false },
          { id: "rt-projects", label: "1h projects", addToSkills: false },
          { id: "rt-apps", label: "30 min applications", addToSkills: false },
          { id: "rt-messages", label: "10–20 min recruiter messages", addToSkills: false },
        ],
      },
      {
        id: "routine-weekend",
        title: "Sat–Sun",
        items: [
          {
            id: "rt-sat",
            label: "Saturday: portfolio, GitHub, cloud + ETL review",
            addToSkills: false,
          },
          {
            id: "rt-sun",
            label: "Sunday: interview practice, review, plan",
            addToSkills: false,
          },
        ],
      },
    ],
  },
  {
  id: "phase12",
  title: "Phase 12 — Git, CLI & Testing",
  description: "Version control, command line, environment management, data quality checks.",
  sections: [
    {
      id: "git-core",
      title: "Git & Version Control",
      items: [
        { id: "git-basics", label: "Git basics: add, commit, push, pull", addToSkills: true },
        { id: "git-branching", label: "Branches, merge, pull requests", addToSkills: true },
        { id: "git-workflow", label: "Feature branches & workflow", addToSkills: true }
      ]
    },
    {
      id: "cli-core",
      title: "Command Line",
      items: [
        { id: "cli-commands", label: "cd, ls, mkdir, rm, run scripts", addToSkills: true },
        { id: "cli-env", label: "Virtual envs (venv / conda)", addToSkills: true },
        { id: "cli-dotenv", label: ".env management & logging", addToSkills: true }
      ]
    },
    {
      id: "testing-quality",
      title: "Testing & Data Quality",
      items: [
        { id: "dq-checks", label: "Data quality checks (nulls, ranges, schema)", addToSkills: true },
        { id: "py-pytest", label: "pytest basics for pipeline testing", addToSkills: true },
        { id: "pipeline-alerts", label: "Alerts & simple monitoring", addToSkills: true }
      ]
    }
  ]
  },
  {
  id: "phase13",
  title: "Phase 13 — Deep Learning Foundations",
  description: "Neural networks, training loops, optimization, and evaluation.",
  sections: [
    {
      id: "dl-core",
      title: "Neural Network Basics",
      items: [
        { id: "dl-nn-basics", label: "Neurons, layers, activation functions", addToSkills: true },
        { id: "dl-forward-back", label: "Forward & backward propagation", addToSkills: true },
        { id: "dl-loss-optim", label: "Loss functions & optimizers (SGD, Adam)", addToSkills: true }
      ]
    },
    {
      id: "dl-engineering",
      title: "ML Engineering",
      items: [
        { id: "dl-metrics", label: "Precision, recall, F1, ROC-AUC", addToSkills: true },
        { id: "dl-feature-eng", label: "Feature engineering pipelines", addToSkills: true },
        { id: "dl-hyperparam", label: "Hyperparameter tuning", addToSkills: true }
      ]
    },
    {
  id: "phase13-projects",
  title: "Projects",
  items: [
    { id: "p13-nn-numpy", label: "Neural Network From Scratch (NumPy)", addToSkills: false },
    { id: "p13-mnist", label: "MNIST Digit Classifier (90–95% accuracy)", addToSkills: false }
  ]
}
  ]
},
{
  id: "phase14",
  title: "Phase 14 — PyTorch & TensorFlow",
  description: "Deep learning frameworks for training and deploying AI models.",
  sections: [
    {
      id: "pytorch-core",
      title: "PyTorch",
      items: [
        { id: "pt-tensors", label: "Tensors, autograd, gradients", addToSkills: true },
        { id: "pt-models", label: "Building & training models", addToSkills: true },
        { id: "pt-training", label: "Training loops, optimizers, checkpoints", addToSkills: true }
      ]
    },
    {
      id: "tf-core",
      title: "TensorFlow & Keras",
      items: [
        { id: "tf-basics", label: "Sequential & functional models", addToSkills: true },
        { id: "tf-training", label: "Callbacks, early stopping, logging", addToSkills: true }
      ]
    },
    {
  id: "phase14-projects",
  title: "Projects",
  items: [
    { id: "p14-pytorch-img", label: "PyTorch Image Classifier (Transfer Learning)", addToSkills: false },
    { id: "p14-tf-text", label: "TensorFlow Text Classifier", addToSkills: false }
  ]
}
  ]
},
{
  id: "phase15",
  title: "Phase 15 — NLP & Transformers",
  description: "Text processing, embeddings, transformers, LLM fundamentals.",
  sections: [
    {
      id: "nlp-core",
      title: "NLP Foundations",
      items: [
        { id: "nlp-tokenization", label: "Tokenization, stemming, lemmatization", addToSkills: true },
        { id: "nlp-embeddings", label: "Word embeddings (Word2Vec, GloVe)", addToSkills: true }
      ]
    },
    {
      id: "transformers-core",
      title: "Transformers",
      items: [
        { id: "tf-bert", label: "BERT basics & fine-tuning", addToSkills: true },
        { id: "tf-gpt", label: "GPT-style models and embeddings", addToSkills: true },
        { id: "tf-classification", label: "Text classification project", addToSkills: false }
      ]
    },
    {
  id: "phase15-projects",
  title: "Projects",
  items: [
    { id: "p15-bert", label: "BERT Fine-Tuning Text Classifier", addToSkills: false },
    { id: "p15-gpt-search", label: "GPT Embedding Semantic Search Engine", addToSkills: false }
  ]
}
  ]
},
{
  id: "phase16",
  title: "Phase 16 — Model Deployment & MLOps",
  description: "Deploy models, build APIs, use cloud ML tools.",
  sections: [
    {
      id: "deploy-core",
      title: "Deployment",
      items: [
        { id: "deploy-fastapi", label: "Deploy model with FastAPI", addToSkills: true },
        { id: "deploy-docker", label: "Docker basics: build & run containers", addToSkills: true },
        { id: "deploy-cloud", label: "Deploy on AWS/GCP (SageMaker/Vertex)", addToSkills: true }
      ]
    },
    {
      id: "mlops-core",
      title: "MLOps Basics",
      items: [
        { id: "mlops-tracking", label: "Model tracking (MLflow)", addToSkills: true },
        { id: "mlops-version", label: "Versioning datasets & models", addToSkills: true },
        { id: "mlops-monitor", label: "Monitoring & drift detection", addToSkills: true }
      ]
    },
    {
  id: "phase16-projects",
  title: "Projects",
  items: [
    { id: "p16-fastapi", label: "FastAPI Model Deployment", addToSkills: false },
    { id: "p16-docker-app", label: "Dockerized ML App", addToSkills: false },
    { id: "p16-mlflow", label: "MLflow Tracking & Model Versioning", addToSkills: false }
  ]
}
  ]
},
{
  id: "phase17",
  title: "Phase 17 — Business & KPI Thinking",
  description: "Business metrics, experimentation, and storytelling.",
  sections: [
    {
      id: "biz-kpi",
      title: "Business Metrics",
      items: [
        { id: "kpi-finance", label: "Revenue, margin, AOV, churn", addToSkills: true },
        { id: "kpi-ops", label: "Efficiency, utilization, throughput", addToSkills: true },
        { id: "kpi-marketing", label: "CTR, CAC, LTV", addToSkills: true }
      ]
    },
    {
      id: "biz-story",
      title: "Storytelling",
      items: [
        { id: "story-arc", label: "Problem → Insight → Action", addToSkills: true },
        { id: "story-slide", label: "Clean slide design & exec summaries", addToSkills: true }
      ]
    },
    {
      id: "biz-experiment",
      title: "A/B Testing",
      items: [
        { id: "ab-design", label: "Experiment design & metrics", addToSkills: true },
        { id: "ab-analysis", label: "Interpreting results for decisions", addToSkills: true }
      ]
    },
    {
  id: "phase17-projects",
  title: "Projects",
  items: [
    { id: "p17-kpi-deck", label: "Executive KPI Dashboard + Deck", addToSkills: false },
    { id: "p17-ab-story", label: "A/B Test Story Report", addToSkills: false },
    { id: "p17-revenue-drop", label: "Revenue Drop Investigation Case Study", addToSkills: false }
  ]
}
  ]
}






];

// ===== HELPERS =====
function getAllItems(plan) {
  const items = [];

  plan.forEach((phase) =>
    phase.sections.forEach((section) =>
      section.items.forEach((item) =>
        items.push({
          ...item,
          phaseId: phase.id,
          phaseTitle: phase.title,
          sectionId: section.id,
          sectionTitle: section.title,
        })
      )
    )
  );

  return items;
}

const ALL_ITEMS = getAllItems(PLAN);

// ===== COMPONENT =====
const SkillTracker = () => {
  const [completedIds, setCompletedIds] = useState([]);
  const [customSkills, setCustomSkills] = useState([]);
  const [newCustomLabel, setNewCustomLabel] = useState("");
  const [newCustomPhase, setNewCustomPhase] = useState(PLAN[0]?.id || "phase1");
  const [loading, setLoading] = useState(true);

  // ---- load from API with localStorage fallback ----
  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/skills/progress`);

        if (!res.ok) {
          throw new Error("Failed to load skill progress");
        }

        const data = await res.json();

        setCompletedIds(Array.isArray(data.completed) ? data.completed : []);
        setCustomSkills(Array.isArray(data.customSkills) ? data.customSkills : []);

        localStorage.setItem(
          "skillTracker.completedIds",
          JSON.stringify(Array.isArray(data.completed) ? data.completed : [])
        );

        localStorage.setItem(
          "skillTracker.customSkills",
          JSON.stringify(Array.isArray(data.customSkills) ? data.customSkills : [])
        );
      } catch (err) {
        console.error(
          "SkillTracker: API load failed, using localStorage fallback",
          err
        );

        const rawCompleted = localStorage.getItem("skillTracker.completedIds");
        const rawCustom = localStorage.getItem("skillTracker.customSkills");

        setCompletedIds(rawCompleted ? JSON.parse(rawCompleted) : []);
        setCustomSkills(rawCustom ? JSON.parse(rawCustom) : []);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // ---- save to backend + localStorage ----
  const saveProgress = async (newCompleted, newCustom) => {
    localStorage.setItem(
      "skillTracker.completedIds",
      JSON.stringify(newCompleted)
    );

    localStorage.setItem(
      "skillTracker.customSkills",
      JSON.stringify(newCustom)
    );

    try {
      const res = await fetch(`${API_BASE}/api/skills/progress`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          completed: newCompleted,
          customSkills: newCustom,
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to save progress");
      }
    } catch (err) {
      console.error("SkillTracker: failed to save progress to API", err);
    }
  };

  // ---- toggle built-in roadmap item ----
  const toggleItem = (id) => {
    setCompletedIds((prev) => {
      const set = new Set(prev);

      if (set.has(id)) {
        set.delete(id);
      } else {
        set.add(id);
      }

      const updatedCompleted = Array.from(set);

      saveProgress(updatedCompleted, customSkills);

      return updatedCompleted;
    });
  };

  // ---- toggle custom skill ----
  const toggleCustom = (id) => {
    setCustomSkills((prev) => {
      const updatedCustomSkills = prev.map((skill) =>
        skill.id === id
          ? {
              ...skill,
              completed: !skill.completed,
            }
          : skill
      );

      saveProgress(completedIds, updatedCustomSkills);

      return updatedCustomSkills;
    });
  };

  // ---- delete custom skill ----
  const deleteCustom = (id) => {
    setCustomSkills((prev) => {
      const updatedCustomSkills = prev.filter((skill) => skill.id !== id);

      saveProgress(completedIds, updatedCustomSkills);

      return updatedCustomSkills;
    });
  };

  // ---- add custom skill under selected phase ----
  const handleAddCustomSkill = () => {
    const label = newCustomLabel.trim();

    if (!label) {
      return;
    }

    const newSkill = {
      id: `custom-${Date.now()}`,
      label,
      phaseId: newCustomPhase,
      completed: false,
    };

    setCustomSkills((prev) => {
      const updatedCustomSkills = [...prev, newSkill];

      saveProgress(completedIds, updatedCustomSkills);

      return updatedCustomSkills;
    });

    setNewCustomLabel("");
  };

  // ---- stats ----
  const totalItems = ALL_ITEMS.length;
  const completedCount = completedIds.length;

  const overallProgress = totalItems
    ? Math.round((completedCount / totalItems) * 100)
    : 0;

  const phaseProgress = useMemo(() => {
    const result = {};

    PLAN.forEach((phase) => {
      const phaseItems = ALL_ITEMS.filter((item) => item.phaseId === phase.id);

      const phaseCustomSkills = customSkills.filter(
        (skill) => skill.phaseId === phase.id
      );

      const total = phaseItems.length + phaseCustomSkills.length;

      const builtInDone = phaseItems.filter((item) =>
        completedIds.includes(item.id)
      ).length;

      const customDone = phaseCustomSkills.filter(
        (skill) => skill.completed
      ).length;

      const done = builtInDone + customDone;

      result[phase.id] = {
        total,
        done,
        percent: total ? Math.round((done / total) * 100) : 0,
      };
    });

    return result;
  }, [completedIds, customSkills]);

  const mySkills = useMemo(() => {
    const builtInSkills = ALL_ITEMS.filter(
      (item) => item.addToSkills && completedIds.includes(item.id)
    );

    const completedCustomSkills = customSkills
      .filter((skill) => skill.completed)
      .map((skill) => {
        const phase = PLAN.find((p) => p.id === skill.phaseId);

        return {
          id: skill.id,
          label: skill.label,
          phaseTitle: phase ? phase.title : "Custom",
        };
      });

    return [...builtInSkills, ...completedCustomSkills];
  }, [completedIds, customSkills]);

  if (loading) {
    return (
      <div className="track-wrapper">
        <h2 className="track-title">Data Analyst Mastery Tracker</h2>
        <p>Loading your progress...</p>
      </div>
    );
  }

  return (
    <div className="track-wrapper">
      <h2 className="track-title">Data Analyst Mastery Tracker</h2>

      <p className="track-subtitle">
        Check items only when you can use them in a real project or interview.
        This updates your skill set and progress bars.
      </p>

      {/* OVERALL PROGRESS */}
      <div className="track-summary">
        <div className="summary-main">
          <div className="summary-label">Overall Progress</div>

          <div className="summary-percent">{overallProgress}%</div>

          <div className="summary-count">
            {completedCount} / {totalItems} built-in skills & tasks
          </div>
        </div>

        <div className="summary-bar">
          <div
            className="summary-bar-fill"
            style={{
              width: `${overallProgress}%`,
            }}
          />
        </div>
      </div>

      <div className="track-layout">
        {/* LEFT SIDE: PHASES */}
        <div className="track-phases">
          {PLAN.map((phase) => {
            const prog = phaseProgress[phase.id] || {
              total: 0,
              done: 0,
              percent: 0,
            };

            const phaseCustomSkills = customSkills.filter(
              (skill) => skill.phaseId === phase.id
            );

            return (
              <div key={phase.id} className="phase-card">
                <div className="phase-header">
                  <div>
                    <div className="phase-title">{phase.title}</div>
                    <div className="phase-desc">{phase.description}</div>
                  </div>

                  <div className="phase-progress">
                    <span className="phase-progress-percent">
                      {prog.percent}%
                    </span>

                    <span className="phase-progress-count">
                      {prog.done}/{prog.total}
                    </span>
                  </div>
                </div>

                <div className="phase-progress-bar">
                  <div
                    className="phase-progress-bar-fill"
                    style={{
                      width: `${prog.percent}%`,
                    }}
                  />
                </div>

                {/* BUILT-IN SECTIONS */}
                {phase.sections.map((section) => (
                  <div key={section.id} className="phase-section">
                    <div className="section-title">{section.title}</div>

                    <ul className="section-list">
                      {section.items.map((item) => (
                        <li key={item.id} className="skill-item">
                          <label>
                            <input
                              type="checkbox"
                              checked={completedIds.includes(item.id)}
                              onChange={() => toggleItem(item.id)}
                            />

                            <span>{item.label}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {/* CUSTOM SKILLS INSIDE THIS PHASE */}
                {phaseCustomSkills.length > 0 && (
                  <div className="phase-section">
                    <div className="section-title">Custom Skills</div>

                    <ul className="section-list">
                      {phaseCustomSkills.map((skill) => (
                        <li key={skill.id} className="skill-item custom-item">
                          <label>
                            <input
                              type="checkbox"
                              checked={skill.completed}
                              onChange={() => toggleCustom(skill.id)}
                            />

                            <span>{skill.label}</span>
                          </label>

                          <button
                            type="button"
                            className="custom-delete-btn"
                            onClick={() => deleteCustom(skill.id)}
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT SIDE */}
        <div className="track-side">
          {/* COMPLETED SKILLS */}
          <div className="side-card">
            <div className="side-title">My Completed Skills</div>

            {mySkills.length === 0 ? (
              <div className="side-empty">
                No skills marked complete yet. Start from Phase 1.
              </div>
            ) : (
              <ul className="skills-list">
                {mySkills.map((skill) => (
                  <li key={skill.id} className="skills-item">
                    <span className="skills-label">{skill.label}</span>
                    <span className="skills-phase">{skill.phaseTitle}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ADD CUSTOM SKILL FORM ONLY */}
          <div className="side-card">
            <div className="side-title">Add Custom Skill</div>

            <div className="custom-form">
              <input
                className="custom-input"
                placeholder="New skill to learn..."
                value={newCustomLabel}
                onChange={(e) => setNewCustomLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddCustomSkill();
                  }
                }}
              />

              <select
                className="custom-select"
                value={newCustomPhase}
                onChange={(e) => setNewCustomPhase(e.target.value)}
              >
                {PLAN.map((phase) => (
                  <option key={phase.id} value={phase.id}>
                    {phase.title}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="custom-add-btn"
                onClick={handleAddCustomSkill}
              >
                + Add
              </button>
            </div>

            <div className="side-empty">
              Pick a phase, type the custom skill, then click Add. It will show
              inside that phase under Custom Skills.
            </div>
          </div>

          {/* HOW TO USE */}
          <div className="side-card">
            <div className="side-title">How to Use This</div>

            <ul className="usage-list">
              <li>Only check items you can explain and use in a real problem.</li>
              <li>Use Phase 11 as your weekly schedule.</li>
              <li>
                When a phase hits 80–100%, you can confidently put it in your
                resume and talk about it in interviews.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillTracker;